import types from '../mutation-types';
import DataAssistantAPI from '../../api/dataAssistant';

export const state = {
  conversations: [],
  currentConversation: null,
  messages: [],
  progressMessage: null,
  uiFlags: {
    isFetchingConversations: false,
    isFetchingMessages: false,
    isCreating: false,
    isAsking: false,
  },
};

export const getters = {
  getConversations: _state => _state.conversations,
  getCurrentConversation: _state => _state.currentConversation,
  getMessages: _state => _state.messages,
  getUIFlags: _state => _state.uiFlags,
  getProgressMessage: _state => _state.progressMessage,
};

export const actions = {
  fetchConversations: async ({ commit }) => {
    commit(types.SET_DATA_ASSISTANT_UI_FLAG, { isFetchingConversations: true });
    try {
      const { data } = await DataAssistantAPI.get();
      commit(types.SET_DATA_ASSISTANT_CONVERSATIONS, data);
    } catch (error) {
      // ignore
    } finally {
      commit(types.SET_DATA_ASSISTANT_UI_FLAG, {
        isFetchingConversations: false,
      });
    }
  },

  fetchConversation: async ({ commit }, id) => {
    commit(types.SET_DATA_ASSISTANT_UI_FLAG, { isFetchingMessages: true });
    try {
      const { data } = await DataAssistantAPI.show(id);
      commit(types.SET_DATA_ASSISTANT_CURRENT_CONVERSATION, data);
      commit(types.SET_DATA_ASSISTANT_MESSAGES, data.messages || []);
    } catch (error) {
      // ignore
    } finally {
      commit(types.SET_DATA_ASSISTANT_UI_FLAG, { isFetchingMessages: false });
    }
  },

  createConversation: async ({ commit }, title = null) => {
    commit(types.SET_DATA_ASSISTANT_UI_FLAG, { isCreating: true });
    try {
      const { data } = await DataAssistantAPI.create({ title });
      commit(types.ADD_DATA_ASSISTANT_CONVERSATION, data);
      return data;
    } finally {
      commit(types.SET_DATA_ASSISTANT_UI_FLAG, { isCreating: false });
    }
  },

  deleteConversation: async ({ commit }, id) => {
    try {
      await DataAssistantAPI.delete(id);
      commit(types.DELETE_DATA_ASSISTANT_CONVERSATION, id);
    } catch (error) {
      throw new Error(error);
    }
  },

  updateMessage: async ({ commit }, { conversationId, messageId, content }) => {
    const { data } = await DataAssistantAPI.updateMessage(
      conversationId,
      messageId,
      { content }
    );
    commit(types.UPDATE_DATA_ASSISTANT_MESSAGE, data);
    return data;
  },

  ask: async ({ commit }, { conversationId, question }) => {
    commit(types.SET_DATA_ASSISTANT_UI_FLAG, { isAsking: true });
    commit(types.SET_DATA_ASSISTANT_PROGRESS, null);
    try {
      const { data } = await DataAssistantAPI.ask(conversationId, question);
      // Server returns the user message; add it to the list.
      // The assistant response will arrive via ActionCable.
      commit(types.ADD_DATA_ASSISTANT_MESSAGE, data);
    } catch (error) {
      commit(types.ADD_DATA_ASSISTANT_MESSAGE, {
        role: 'assistant',
        content: 'Request failed. Please try again.',
        result_type: 'error',
        error_message: error?.response?.data?.error || error.message,
        created_at: new Date().toISOString(),
      });
      commit(types.SET_DATA_ASSISTANT_UI_FLAG, { isAsking: false });
      throw error;
    }
  },

  receiveMessage: ({ commit, state: _state }, data) => {
    if (_state.currentConversation?.id !== data.conversation_id) return;

    if (data.progress) {
      commit(types.SET_DATA_ASSISTANT_PROGRESS, data.content);
    } else {
      commit(types.ADD_DATA_ASSISTANT_MESSAGE, data);
      commit(types.SET_DATA_ASSISTANT_PROGRESS, null);
      commit(types.SET_DATA_ASSISTANT_UI_FLAG, { isAsking: false });
    }
  },
};

export const mutations = {
  [types.SET_DATA_ASSISTANT_UI_FLAG](_state, flags) {
    _state.uiFlags = { ..._state.uiFlags, ...flags };
  },
  [types.SET_DATA_ASSISTANT_CONVERSATIONS](_state, data) {
    _state.conversations = data;
  },
  [types.ADD_DATA_ASSISTANT_CONVERSATION](_state, data) {
    _state.conversations.unshift(data);
  },
  [types.DELETE_DATA_ASSISTANT_CONVERSATION](_state, id) {
    _state.conversations = _state.conversations.filter(c => c.id !== id);
    if (_state.currentConversation?.id === id) {
      _state.currentConversation = null;
      _state.messages = [];
    }
  },
  [types.SET_DATA_ASSISTANT_CURRENT_CONVERSATION](_state, data) {
    _state.currentConversation = data;
  },
  [types.SET_DATA_ASSISTANT_MESSAGES](_state, data) {
    _state.messages = data;
  },
  [types.ADD_DATA_ASSISTANT_MESSAGE](_state, data) {
    _state.messages.push(data);
  },
  [types.UPDATE_DATA_ASSISTANT_MESSAGE](_state, data) {
    _state.messages = _state.messages.map(m => (m.id === data.id ? data : m));
  },
  [types.SET_DATA_ASSISTANT_PROGRESS](_state, text) {
    _state.progressMessage = text;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
